import React, { useEffect, useRef } from 'react';
import { NumericFormat } from 'react-number-format';

import InputBase from 'apps/auth/components/form-multi/InputBase';
import { validationAndUpdateValue } from 'apps/auth/components/form-multi/utils';
import { COMMON_AMOUNT_ISSUE, MAX_AMOUNT_ISSUE, MIN_AMOUNT_ISSUE, MULTIPLE_AMOUNT_ISSUE } from 'apps/auth/constants/Constants';
import { inputPropType } from 'apps/auth/helpers/customPropTypes';
import { resolveArguments } from 'apps/auth/helpers/utils';
import { useApp, useLanguage } from 'apps/auth/hooks/selectors';
import { useNumberSeparators } from 'apps/auth/hooks/useNumberSeparators';

const InputAmount = (props) => {
    const { country } = useApp();
    const { lang } = useLanguage();
    const firstRender = useRef(true);
    const hasError = useRef(false);
    const { thousandSeparator, decimalSeparator } = useNumberSeparators(lang, country);

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        if (props.field.value && props.enabledValidateMinMaxAmount) {
            const value = props.field.value;
            validationAndUpdateValue(props.formName, value, props.field, props.changeFormField).then(() => {
                if (props.enabledValidateMinMaxAmount) {
                    validateMinMaxMultipleAmount(parseFloat(value ? value.replaceAll(' ', '') : value));
                }
            });
        }
    }, [props.minAmount?.amount, props.maxAmount?.amount]);

    const handlerChange = (value) => {
        const floatValue = value.floatValue;
        props.setAmountFromFloatValue?.(floatValue);
        if (props.googlePay) {
            const totalPriceObject = { value: floatValue };
            window.localStorage.setItem('totalPrice', JSON.stringify(totalPriceObject));
        }

        validationAndUpdateValue(props.formName, floatValue, props.field, props.changeFormField).then(() => {
            if (props.enabledValidateMinMaxAmount) {
                validateMinMaxMultipleAmount(floatValue);
            }
            if (props.onChangeHandler) {
                props.onChangeHandler(floatValue, props.field.name);
            }
        });
    };

    const validateMinMaxMultipleAmount = (value) => {
        let errorCode = COMMON_AMOUNT_ISSUE;
        const min = props.minAmount?.amount;
        const max = props.maxAmount?.amount;
        const formattedMin = props.minAmount?.formatedAmount;
        const formattedMax = props.maxAmount?.formatedAmount;
        const multiple = props.multiple;
        let errorMessage;
        if (value < min) {
            errorMessage = resolveArguments(props.jsMessages['site.js.validation.min.valid.error'], formattedMin);
            errorCode = MIN_AMOUNT_ISSUE;
        } else if (value > max) {
            errorMessage = resolveArguments(props.jsMessages['site.js.validation.max.valid.error'], formattedMax);
            errorCode = MAX_AMOUNT_ISSUE;
            // no prop passed
        } else if (multiple && value % multiple !== 0) {
            errorMessage = resolveArguments(props.jsMessages['site.js.validation.multiple.error'], multiple);
            errorCode = MULTIPLE_AMOUNT_ISSUE;
        }
        if (errorMessage) {
            props.setFieldError(props.formName, props.field.name, { body: errorMessage, code: errorCode });
        }
        hasError.current = !!errorMessage;
    };

    const handlerBlur = (e) => {
        const field = props.field;
        const floatValue = field.value;

        if (!hasError.current) {
            validationAndUpdateValue(props.formName, floatValue, field, props.changeFormField).then(() => {
                callBlurHandler(field, floatValue);
            });
        } else {
            callBlurHandler(field, floatValue);
        }

        if (props.onBlur) {
            props.onBlur(e);
        }
    };

    const callBlurHandler = (field, cleanValue) => {
        if (props.onBlurHandler) {
            props.onBlurHandler(cleanValue, field.name, field.checkRegexp, field.mandatory);
        }
    };

    const handlerFocus = (e) => {
        if (props.onFocusHandler) {
            props.onFocusHandler(e);
        }

        if (props.onFocus) {
            props.onFocus(e);
        }
    };

    const field = props.field;
    if (field.value === null) {
        field.value = '';
    }
    const dataTestAttr = props.isLanding && props.isFrom ? '-from' : props.isLanding && !props.isFrom ? '-to' : '';
    return (
        <NumericFormat
            id={field.name}
            maxLength="14"
            name={field.canonicalPath ? field.canonicalPath : field.name}
            value={field.value || ''}
            type={field.type}
            readOnly={props.readOnly}
            className={props.isNewInput ? null : `field field-duo${props.classNameError ? ' color-red' : ''}`}
            autoComplete="off"
            allowNegative={false}
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            fixedDecimalScale={true}
            data-testid={`input-amount-number${dataTestAttr}`}
            decimalScale={2}
            onValueChange={handlerChange}
            onBlur={handlerBlur}
            onFocus={handlerFocus}
        />
    );
};

InputAmount.propTypes = inputPropType(['number', 'tel']);

export default InputBase(InputAmount, true);
